import { getCountriesList, getStatesList } from "../countryStateData.js"
import { AsYouType } from 'libphonenumber-js'

/**
 * Name is a combination of a first name and a last name.
 * Ported from C# ContactService.cs
 * @typedef {Object} Name
 * @property {string|null} first - The first name
 * @property {string|null} last - The last name
 * @property {function(): string} full - Returns the full name as a string
 */
export class Name {
  /**
   * @param {string|null} first - The first name
   * @param {string|null} last - The last name
   */
  constructor(first = null, last = null) {
    this.first = first;
    this.last = last;
  }

  /**
   * Returns the full name as a string
   * Ported from C# ContactService.cs
   * @returns {string} The full name with first and last names combined
   */
  full() {
    return `${this.first ?? ""} ${this.last ?? ""}`.trim();
  }

  /**
   * Friendly display name for greetings.
   * Ported from C# ContactService.cs
   * @returns {string} First name, or "M. {last}", or "Customer"
   */
  friendly() {
    const first = (this.first ?? "").trim();
    const last = (this.last ?? "").trim();
    if (first.length > 0) return first;
    if (last.length > 0) return `M. ${last}`;
    return "Customer";
  }

  lastCommaFirst() {
    return `${[this.last ?? "", this.first ?? ""].filter(Boolean).join(", ")}`.trim(); // Only include non-empty values
  }
}

/**
 * Email contact is a combination of a name and an email address.
 * Ported from C# ContactService.cs
 * @typedef {Object} EmailContact
 * @property {Name} name - The name object
 * @property {string|null} email - The email address
 * @property {string[]} roles - The roles this contact serves (billing, shipping, etc.)
 */
export class EmailContact {
  /**
   * @param {Object} options - The contact options
   * @param {Name} options.name - The name object
   * @param {string|null} options.email - The email address
   * @param {string[]} options.roles - The roles this contact serves
   */
  constructor({ name = new Name(), email = null, roles = [] } = {}) {
    this.name = name;
    this.email = email;
    this.roles = roles;
  }
}

/**
 * Phone contact is a combination of a name and a phone number.
 * Ported from C# ContactService.cs
 * @typedef {Object} PhoneContact
 * @property {Name} name - The name object
 * @property {string|null} phone - The phone number
 * @property {string[]} roles - The roles this contact serves (billing, shipping, etc.)
 */
export class PhoneContact {
  /**
   * @param {Object} options - The contact options
   * @param {Name} options.name - The name object
   * @param {string|null} options.phone - The phone number
   * @param {string[]} options.roles - The roles this contact serves
   */
  constructor({ name = new Name(), phone = null, roles = [] } = {}) {
    this.name = name;
    this.phone = phone;
    this.roles = roles;
  }
}

/**
 * Centralized service for handling address/contact-related operations on orders.
 * This service provides consistent address/contact resolution logic across the application.
 * 
 * Ported from C# ContactService.cs
 */
export class ContactService {

  // Ported from C# ContactService.cs
  static ROLE_SHIPPING = "shipping";
  static ROLE_BILLING = "billing";

  static COUNTRY_ISO2_UNITED_STATES = "US";
  static COUNTRY_ISO2_CANADA = "CA";

  static TAX_REGION_CODE_PICK_UP = "BC";
  static TAX_REGION_CODE_UNITED_STATES = "US";
  static TAX_REGION_CODE_INTERNATIONAL = "IN";

  /**
   * Get the primary email contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {EmailContact|null} The primary email contact or null if none found
   */
  static getPrimaryEmailContact(order) {
    let emailContact = ContactService.getEmailContactByRole(order, ContactService.ROLE_BILLING);
    if (emailContact != null) {
      return emailContact;
    }
    if (!ContactService.isPickUp(order)) {
      emailContact = ContactService.getEmailContactByRole(order, ContactService.ROLE_SHIPPING);
      if (emailContact != null) {
        return emailContact;
      }
    }
    return null;
  }

  /**
   * Get the primary phone contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {PhoneContact|null} The primary phone contact or null if none found
   */
  static getPrimaryPhoneContact(order) {
    let phoneContact = ContactService.getPhoneContactByRole(order, ContactService.ROLE_BILLING);
    if (phoneContact != null) {
      return phoneContact;
    }
    if (!ContactService.isPickUp(order)) {
      phoneContact = ContactService.getPhoneContactByRole(order, ContactService.ROLE_SHIPPING);
      if (phoneContact != null) {
        return phoneContact;
      }
    }
    return null;
  }

  /**
   * Gets all email contacts for an order, merging duplicates
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {EmailContact[]} Array of unique email contacts with merged roles
   */
  static getAllEmailContacts(order) {
    const emailContacts = [];
    const billingContact = ContactService.getEmailContactByRole(order, ContactService.ROLE_BILLING);
    if (billingContact != null) {
      emailContacts.push(billingContact);
    }
    if (!ContactService.isPickUp(order)) {
      const shippingContact = ContactService.getEmailContactByRole(order, ContactService.ROLE_SHIPPING);
      if (shippingContact != null) {
        emailContacts.push(shippingContact);
      }
    }

    return ContactService.#mergeDuplicateEmailContacts(emailContacts);
  }

  /**
   * Gets all phone contacts for an order, merging duplicates
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {PhoneContact[]} Array of unique phone contacts with merged roles
   */
  static getAllPhoneContacts(order) {
    const phoneContacts = [];
    const billingContact = ContactService.getPhoneContactByRole(order, ContactService.ROLE_BILLING);
    if (billingContact != null) {
      phoneContacts.push(billingContact);
    }
    if (!ContactService.isPickUp(order)) {
      const shippingContact = ContactService.getPhoneContactByRole(order, ContactService.ROLE_SHIPPING);
      if (shippingContact != null) {
        phoneContacts.push(shippingContact);
      }
    }

    return ContactService.#mergeDuplicatePhoneContacts(phoneContacts);
  }

  /**
   * Get the billing email contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {EmailContact|null} The billing email contact or null if none found
   */
  static getBillingEmailContact(order) {
    return ContactService.getEmailContactByRole(order, ContactService.ROLE_BILLING);
  }

  /**
   * Get the shipping email contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {EmailContact|null} The shipping email contact or null if none found
   */
  static getShippingEmailContact(order) {
    return ContactService.getEmailContactByRole(order, ContactService.ROLE_SHIPPING);
  }

  /**
   * Get the billing phone contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {PhoneContact|null} The billing phone contact or null if none found
   */
  static getBillingPhoneContact(order) {
    return ContactService.getPhoneContactByRole(order, ContactService.ROLE_BILLING);
  }

  /**
   * Get the shipping phone contact for the order.
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {PhoneContact|null} The shipping phone contact or null if none found
   */
  static getShippingPhoneContact(order) {
    return ContactService.getPhoneContactByRole(order, ContactService.ROLE_SHIPPING);
  }

  /**
   * Get email contact by role (billing or shipping).
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @param {string} role - The role ("billing" or "shipping")
   * @returns {EmailContact|null} The email contact or null if none found
   * @throws {Error} If role is invalid or if trying to get shipping contact for pickup order
   */
  static getEmailContactByRole(order, role) {
    switch (role) {
      case ContactService.ROLE_BILLING:
        if (order.bill_to != null && order.bill_to.email && order.bill_to.email.trim() !== '') {
          return new EmailContact({
            name: new Name(order.bill_to.first_name, order.bill_to.last_name),
            email: order.bill_to.email,
            roles: order.bill_same_as_ship ? [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING] : [ContactService.ROLE_BILLING]
          });
        }
        if (order.bill_same_as_ship && order.ship_to != null && order.ship_to.email && order.ship_to.email.trim() !== '') {
          return new EmailContact({
            name: new Name(order.ship_to.first_name, order.ship_to.last_name),
            email: order.ship_to.email,
            roles: [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING]
          });
        }
        return null;
      case ContactService.ROLE_SHIPPING:
        if (ContactService.isPickUp(order)) {
          // If we hit this error it is a bug in the code. Always check isPickUp() before calling this method with ROLE_SHIPPING.
          throw new Error("Cannot get shipping email contact for pick up order");
        }
        if (order.ship_to != null && order.ship_to.email && order.ship_to.email.trim() !== '') {
          return new EmailContact({
            name: new Name(order.ship_to.first_name, order.ship_to.last_name),
            email: order.ship_to.email,
            roles: order.bill_same_as_ship ? [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING] : [ContactService.ROLE_SHIPPING]
          });
        }
        if (order.bill_same_as_ship && order.bill_to != null && order.bill_to.email && order.bill_to.email.trim() !== '') {
          return new EmailContact({
            name: new Name(order.bill_to.first_name, order.bill_to.last_name),
            email: order.bill_to.email,
            roles: [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING]
          });
        }
        return null;
      default:
        throw new Error(`Invalid role: ${role}`);
    }
  }

  /**
   * Get phone contact by role (billing or shipping).
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @param {string} role - The role ("billing" or "shipping")
   * @returns {PhoneContact|null} The phone contact or null if none found
   * @throws {Error} If role is invalid or if trying to get shipping contact for pickup order
   */
  static getPhoneContactByRole(order, role) {
    switch (role) {
      case ContactService.ROLE_BILLING:
        if (order.bill_to != null && order.bill_to.phone && order.bill_to.phone.trim() !== '') {
          return new PhoneContact({
            name: new Name(order.bill_to.first_name, order.bill_to.last_name),
            phone: order.bill_to.phone,
            roles: order.bill_same_as_ship ? [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING] : [ContactService.ROLE_BILLING]
          });
        }
        if (order.bill_same_as_ship && order.ship_to != null && order.ship_to.phone && order.ship_to.phone.trim() !== '') {
          return new PhoneContact({
            name: new Name(order.ship_to.first_name, order.ship_to.last_name),
            phone: order.ship_to.phone,
            roles: [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING]
          });
        }
        return null;
      case ContactService.ROLE_SHIPPING:
        if (ContactService.isPickUp(order)) {
          // If we hit this error it is a bug in the code. Always check isPickUp() before calling this method with ROLE_SHIPPING.
          throw new Error("Cannot get shipping phone contact for pick up order");
        }
        if (order.ship_to != null && order.ship_to.phone && order.ship_to.phone.trim() !== '') {
          return new PhoneContact({
            name: new Name(order.ship_to.first_name, order.ship_to.last_name),
            phone: order.ship_to.phone,
            roles: order.bill_same_as_ship ? [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING] : [ContactService.ROLE_SHIPPING]
          });
        }
        if (order.bill_same_as_ship && order.bill_to != null && order.bill_to.phone && order.bill_to.phone.trim() !== '') {
          return new PhoneContact({
            name: new Name(order.bill_to.first_name, order.bill_to.last_name),
            phone: order.bill_to.phone,
            roles: [ContactService.ROLE_BILLING, ContactService.ROLE_SHIPPING]
          });
        }
        return null;
      default:
        throw new Error(`Invalid role: ${role}`);
    }
  }

  /**
   * Gets the primary name for an order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {Name} The primary name object
   */
  static getPrimaryName(order) {
    if (order.first_name || order.last_name) {
      return new Name(order.first_name, order.last_name);
    }
    if (order.bill_to != null && ((order.bill_to.first_name && order.bill_to.first_name.trim() !== '') || (order.bill_to.last_name && order.bill_to.last_name.trim() !== ''))) {
      return new Name(order.bill_to.first_name, order.bill_to.last_name);
    }
    if (order.ship_to != null && ((order.ship_to.first_name && order.ship_to.first_name.trim() !== '') || (order.ship_to.last_name && order.ship_to.last_name.trim() !== ''))) {
      return new Name(order.ship_to.first_name, order.ship_to.last_name);
    }
    return new Name();
  }

  /**
   * Gets the shipping name for an order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {Name} The shipping name object
   * @throws {Error} If order is a pickup order
   */
  static getShippingName(order) {
    if (ContactService.isPickUp(order)) {
      // If we hit this error it is a bug in the code. Always check isPickUp() before calling this method.
      throw new Error("Cannot get shipping name for pick up order");
    }
    const shippingAddress = ContactService.getShippingAddress(order);
    return new Name(shippingAddress.first_name, shippingAddress.last_name);
  }

  /**
   * Gets the shipping address for an order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {Object} The shipping address object
   * @throws {Error} If order is a pickup order
   */
  static getShippingAddress(order) {
    if (ContactService.isPickUp(order)) {
      // If we hit this error it is a bug in the code. Always check isPickUp() before calling this method.
      throw new Error("Cannot get shipping address for pick up order");
    }
    return order.ship_to ?? {};
  }

  /**
   * Checks if an order is a pickup order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @returns {boolean} True if the order is a pickup order
   */
  static isPickUp(order) {
    if (!order) {
      throw new Error("Cannot check if order is a pickup order if order is null");
    }
    return order.pick_up_location_id != null;
  }

  /**
   * Gets the pickup location for an order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object
   * @param {Array} locations - Array of location objects
   * @returns {Object|null} The pickup location or null if not found
   * @throws {Error} If order is not a pickup order
   */
  static getPickUpLocation(order, locations) {
    if (!ContactService.isPickUp(order)) {
      // If we hit this error it is a bug in the code. Always check isPickUp() before calling this method.
      throw new Error("Cannot get pick up location for non-pick up order");
    }
    return locations.find(x => x.id === order.pick_up_location_id);
  }

  /**
   * Sets the primary name for an order from a full name string
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object to modify
   * @param {string} fullName - The full name string
   * @returns {Object} The modified order object
   */
  static setPrimaryName(order, fullName) {
    // Split by space and set the first name to the first element, the rest is last name
    if (!fullName || fullName.trim() === '') {
      return order;
    }

    const nameParts = fullName.trim().split(/\s+/).filter(part => part.length > 0);

    if (nameParts.length === 0) {
      return order;
    }

    const firstName = nameParts[0];
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    return ContactService.setPrimaryName(order, firstName, lastName);
  }

  /**
   * Sets the primary name for an order from first and last names
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object to modify
   * @param {string} firstName - The first name
   * @param {string} lastName - The last name
   * @returns {Object} The modified order object
   */
  static setPrimaryName(order, firstName, lastName) {
    // Set the primary name based on the order type
    if (ContactService.isPickUp(order) || !order.bill_same_as_ship) {
      // For pickup orders, set the billing address name
      if (order.bill_to == null) {
        order.bill_to = {};
      }
      order.bill_to.first_name = firstName;
      order.bill_to.last_name = lastName;
    } else {
      // For shipping orders, set the shipping address name
      if (order.ship_to == null) {
        order.ship_to = {};
      }
      order.ship_to.first_name = firstName;
      order.ship_to.last_name = lastName;
    }

    return order;
  }

  /**
   * Sets the pickup location for an order
   * Ported from C# ContactService.cs
   * @param {Object} order - The order object to modify
   * @param {Object} location - The location object
   * @param {number} location.id - The location ID
   * @returns {Object} The modified order object
   */
  static setPickUpLocation(order, location) {
    order.pick_up_location_id = location.id;
    return order;
  }

  /**
   * Merges duplicate email contacts based on first name, last name, and email address
   * Ported from C# ContactService.cs
   * @private
   * @param {EmailContact[]} contacts - Array of email contacts to merge
   * @returns {EmailContact[]} Array of merged contacts with combined roles
   */
  static #mergeDuplicateEmailContacts(contacts) {
    const mergedContacts = [];
    for (const contact of contacts) {
      const existingContact = mergedContacts.find(c =>
        c.name.first === contact.name.first &&
        c.name.last === contact.name.last &&
        c.email === contact.email);

      if (existingContact != null) {
        // Merge roles, removing duplicates
        existingContact.roles = [...new Set([...existingContact.roles, ...contact.roles])];
      } else {
        mergedContacts.push(contact);
      }
    }

    return mergedContacts;
  }

  /**
   * Merges duplicate phone contacts based on first name, last name, and phone number
   * Ported from C# ContactService.cs
   * @private
   * @param {PhoneContact[]} contacts - Array of phone contacts to merge
   * @returns {PhoneContact[]} Array of merged contacts with combined roles
   */
  static #mergeDuplicatePhoneContacts(contacts) {
    const mergedContacts = [];
    for (const contact of contacts) {
      const existingContact = mergedContacts.find(c =>
        c.name.first === contact.name.first &&
        c.name.last === contact.name.last &&
        c.phone === contact.phone);

      if (existingContact != null) {
        // Merge roles, removing duplicates
        existingContact.roles = [...new Set([...existingContact.roles, ...contact.roles])];
      } else {
        mergedContacts.push(contact);
      }
    }

    return mergedContacts;
  }

  static getTaxRegionCode(order) {
    if (ContactService.isPickUp(order)) {
      return ContactService.TAX_REGION_CODE_PICK_UP;
    }
    const shippingAddress = ContactService.getShippingAddress(order);

    if (!shippingAddress || shippingAddress.countryObject == null || shippingAddress.stateObject == null) {
      throw new Error("Cannot get tax region code, invalid shipping address");
    }

    if (shippingAddress.countryObject.iso2 === ContactService.COUNTRY_ISO2_CANADA) {
      return shippingAddress.stateObject.state_code;
    } else if (shippingAddress.countryObject.iso2 === ContactService.COUNTRY_ISO2_UNITED_STATES) {
      return ContactService.TAX_REGION_CODE_UNITED_STATES;
    } else {
      return ContactService.TAX_REGION_CODE_INTERNATIONAL;
    }
  }

  static normalizedAddress(address) {
    const countries = getCountriesList()
    address.country = this.normalizeCountry(address.country, countries)
    const states = getStatesList(countries.find((x) => x.name == address.country)?.id)
    address.state = this.normalizeState(address.state, states)
    address.phone = this.formatPhone(address.phone)
    return Promise.resolve(address)
  }

  static normalizeCountry(country, countriesList) {
    if (!country || !countriesList || countriesList.length == 0) {
      return ""
    }

    // This function exists because country used to be a free field
    const match = countriesList.find(x => {
      if (x.name.trim().toLowerCase() === country.trim().toLowerCase()) {
        return true
      }
      if (x.iso2.trim().toLowerCase() === country.trim().toLowerCase()) {
        return true
      }
      if (x.iso3.trim().toLowerCase() === country.trim().toLowerCase()) {
        return true
      }
    })
    if (match) {
      return match.name
    }

    return ""
  }

  static normalizeState(state, stateList) {
    if (!state || !stateList || stateList.length == 0) {
      return ""
    }

    // This function exists because state used to be a free field
    const match = stateList.find(x => {
      if (x.name.trim().toLowerCase() === state.trim().toLowerCase()) {
        return true
      }
      if (x.state_code.trim().toLowerCase() === state.trim().toLowerCase()) {
        return true
      }
      return false
    })
    if (match) {
      return match.state_code
    }

    return ""
  }

  static formatPhone(value) {
    if (!value) {
      return ""
    }
    if (!value || value.startsWith("+") && !value.startsWith("+1")) {
      value = new AsYouType().input(value)
    } else if (value.startsWith("+1")) {
      value = "+1 " + new AsYouType('CA').input(value.slice(2))
    } else {
      value = new AsYouType('CA').input(value)
    }

    if (value.endsWith(")")) {
      value = value.substring(0, value.length - 1)
    } else if (value.endsWith(" ")) {
      value = value.substring(0, value.length - 1)
    }
    return value
  }

  static isUnitedStates(order) {
    if (ContactService.isPickUp(order)) {
      return false;
    }

    if (!order.ship_to || order.ship_to.countryObject == null) {
      throw new Error("Cannot check if order is in the United States, invalid shipping address");
    }
    return order.ship_to.countryObject.iso2 === ContactService.COUNTRY_ISO2_UNITED_STATES;
  }
}